import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  AdPlatformReportingClient,
  Feature,
  SponsoredListingsCampaign,
  SponsoredListingsFlight,
} from 'src/WebApiController';

/**
 * Custom hook to fetch and merge reporting metrics with campaign flights data.
 * Extracts unique campaign IDs, queries flight metrics from the reporting API, and merges them back into flights.
 *
 * @param campaignsAndFlights - Array of [campaign, flight] tuples to enrich with reporting metrics
 * @returns Campaign-flight tuples enriched with reporting metrics (or original data if metrics not available)
 */
export const useCampaignFlightsTableMetricsData = (
  campaignsAndFlights:
    | [SponsoredListingsCampaign, SponsoredListingsFlight][]
    | null
    | undefined
):
  | [SponsoredListingsCampaign, SponsoredListingsFlight][]
  | null
  | undefined => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasReportingMetricsFeature = useUserHasFeature(
    Feature.SponsoredListingsV2_ReportingMetrics
  );

  // Build campaign-flights request for flight metrics query
  const campaignFlights = useMemo(() => {
    const flightsByCampaign = new Map<number, number[]>();
    for (const [campaign, flight] of campaignsAndFlights ?? []) {
      const existing = flightsByCampaign.get(campaign.campaignId) ?? [];
      existing.push(flight.flightId);
      flightsByCampaign.set(campaign.campaignId, existing);
    }
    return Array.from(flightsByCampaign.entries()).map(
      ([campaignId, flightIds]) => ({ campaignId, flightIds })
    );
  }, [campaignsAndFlights]);

  // Determine date range for metrics query
  // Use a very early date for start (to capture all historical data) and current date for end
  const metricsDateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date('2020-01-01'); // Use a fixed early date to capture all historical metrics

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  }, []);

  // Query key for flight metrics
  const flightMetricsQueryKey = useMemo(
    () => [
      'AdPlatformReportingClient.getFlightsMetricsByCampaignIds',
      activeAccountWebClientConfig.activeAccountId,
      campaignFlights,
      metricsDateRange.startDate,
      metricsDateRange.endDate,
    ],
    [
      activeAccountWebClientConfig.activeAccountId,
      campaignFlights,
      metricsDateRange.startDate,
      metricsDateRange.endDate,
    ]
  );

  // Query to fetch flight metrics
  const shouldQueryMetrics = useMemo(
    () =>
      hasReportingMetricsFeature &&
      activeAccountWebClientConfig.activeAccountId != null &&
      campaignFlights.length > 0,
    [
      hasReportingMetricsFeature,
      activeAccountWebClientConfig.activeAccountId,
      campaignFlights.length,
    ]
  );

  const flightMetricsQuery = useQuery({
    queryKey: flightMetricsQueryKey,
    queryFn: async () => {
      if (!shouldQueryMetrics) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new AdPlatformReportingClient(
            activeAccountWebClientConfig
          );
          const result = await client.getFlightsMetricsByCampaignIds({
            campaignFlights: campaignFlights,
            startDate: metricsDateRange.startDate,
            endDate: metricsDateRange.endDate,
          });
          return result;
        },
        (error) => {
          trackError(
            'AdPlatformReportingClient.getFlightsMetricsByCampaignIds',
            error,
            {
              campaignFlights,
              startDate: metricsDateRange.startDate,
              endDate: metricsDateRange.endDate,
            }
          );
        }
      );
    },
    enabled: shouldQueryMetrics,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Merge flight metrics into campaignsAndFlights data
  const campaignsAndFlightsWithMetrics = useMemo(() => {
    if (!campaignsAndFlights || !flightMetricsQuery.data?.flights) {
      return campaignsAndFlights;
    }

    const metricsMap = new Map(
      flightMetricsQuery.data.flights.map((report) => [report.flightId, report])
    );

    return campaignsAndFlights.map(([campaign, flight]) => {
      const report = metricsMap.get(flight.flightId);
      if (!report || !report.metrics) {
        return [campaign, flight] as [
          SponsoredListingsCampaign,
          SponsoredListingsFlight,
        ];
      }

      // Clone the flight and set metrics properties
      const flightWithMetrics: SponsoredListingsFlight = { ...flight };
      flightWithMetrics.historicPotentialImpressions =
        report.metrics.eventImpressions;
      flightWithMetrics.actualImpressions = report.metrics.adImpressions;
      flightWithMetrics.impressionShare =
        (report.metrics.eventImpressions ?? 0) > 0
          ? (report.metrics.adImpressions ?? 0) /
            (report.metrics.eventImpressions ?? 0)
          : null;
      flightWithMetrics.clicks = report.metrics.clicks;
      flightWithMetrics.salesCount = report.metrics.adSalesCount;
      flightWithMetrics.salesProceeds = report.metrics.adSalesProceeds;

      return [campaign, flightWithMetrics] as [
        SponsoredListingsCampaign,
        SponsoredListingsFlight,
      ];
    });
  }, [campaignsAndFlights, flightMetricsQuery.data]);

  return campaignsAndFlightsWithMetrics;
};
